import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface WorkflowGroupContext {
  /** ID of the active workflow box. */
  activeId: string;
  /** Sets the active workflow box. */
  setActive: (id: string) => void;
}

const Context = createContext<WorkflowGroupContext>(null);

export interface WorkflowGroupProps {
  activeId?: string;
}

export const WorkflowGroup = ({ children, ...props }: PropsWithChildren<WorkflowGroupProps>) => {
  const [activeId, setActiveId] = useState<string>(null);
  const setActive = useCallback((id: string) => setActiveId(id), []);

  useEffect(() => {
    setActiveId(props.activeId);
  }, [props.activeId]);

  const context: WorkflowGroupContext = useMemo(
    () => ({
      activeId,
      setActive,
    }),
    [activeId, setActive],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};

export const useWorkflowGroup = () => {
  const context = useContext(Context);
  if (!context) {
    throw Error('useWorkflowGroup must be used within a WorkflowGroup');
  }

  return context;
};
